import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Clock, Users, GraduationCap, Printer, FileDown, Phone, Mail, Loader2, ChevronRight } from "lucide-react";
import { proApi, downloadProPdf } from "@/lib/proApi";
import { fmtDateTime, fmtDate } from "@/lib/api";

const payLabel = (s) => ({ paid: "Bezahlt", payment_requested: "Offen", pending: "Ausstehend", free: "Kostenlos", unpaid: "Offen", open: "Offen", comp: "Freiplatz", waitlist: "Warteliste", failed: "Zahlung fehlgeschlagen" }[s] || s || "—");
const payColor = (s) => (s === "paid" || s === "free" ? "text-green-400" : "text-amber-400");

const ProCourseDetail = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);

  useEffect(() => {
    proApi.get(`/pro/courses/${id}`).then((r) => {
      setData(r.data);
      const up = (r.data.course.sessions || []);
      const now = new Date();
      const next = up.find((s) => s.status !== "cancelled" && new Date(s.start) >= now) || up[0];
      setSel(next?.id || null);
    }).catch(() => {});
  }, [id]);

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#ff0033]" /></div>;
  const c = data.course;
  const isGroup = c.scheduling_type === "course_group";
  const parts = (data.participants[sel] || []);
  const curSession = c.sessions.find((s) => s.id === sel);
  const totalPersons = parts.reduce((s, p) => s + (p.waitlist ? 0 : (p.persons || 1)), 0);
  const waitPersons = parts.reduce((s, p) => s + (p.waitlist ? (p.persons || 1) : 0), 0);

  const dt = curSession ? fmtDate(curSession.start).replace(/\s/g, "-") : "";
  const coach = (curSession?.coach || c.coach || "Trainer").replace(/\s/g, "-");

  return (
    <div data-testid="pro-course-detail">
      <button onClick={() => nav("/pro/courses")} className="flex items-center gap-2 text-neutral-400 hover:text-white text-sm mb-4"><ArrowLeft size={16} /> Zurück</button>

      <div className="bg-[#0F0F0F] border border-white/10 p-6">
        <div className="flex items-center gap-2 mb-2">
          <span className="font-mono-lab text-[9px] uppercase tracking-widest text-[#ff0033] border border-[#ff0033]/40 px-2 py-0.5">{c.category || "Kurs"}</span>
          {isGroup && <span className="font-mono-lab text-[9px] uppercase tracking-widest text-neutral-400 border border-white/20 px-2 py-0.5">Terminserie</span>}
        </div>
        <h1 className="font-heading text-3xl md:text-4xl uppercase tracking-tight text-white">{c.name}</h1>
        {c.description && <p className="text-neutral-400 text-sm mt-3 max-w-2xl leading-relaxed">{c.description}</p>}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2 mt-4 text-sm text-neutral-300">
          <span className="flex items-center gap-2"><MapPin size={14} className="text-[#ff0033]" /> {c.location || "—"}</span>
          <span className="flex items-center gap-2" data-testid="pro-detail-coach"><GraduationCap size={14} className="text-[#ff0033]" /> {(curSession && curSession.coach) || c.coach || "—"}</span>
          <span className="flex items-center gap-2" data-testid="pro-detail-participants"><Users size={14} className="text-[#ff0033]" /> {totalPersons} Teilnehmer{(curSession?.capacity || c.max_participants) ? ` / ${curSession?.capacity || c.max_participants}` : ""}{curSession ? ` (${fmtDate(curSession.start)})` : ""}</span>
          {c.price != null && <span className="flex items-center gap-2 text-neutral-400">Preis: {c.price} €</span>}
        </div>
        {c.internal_notes && (
          <div className="mt-4 bg-[#1A1A1A] border-l-2 border-[#ff0033] p-3 text-sm text-neutral-300"><b className="text-[#ff0033]">Interner Hinweis:</b> {c.internal_notes}</div>
        )}
        <button onClick={() => downloadProPdf(`/pro/courses/${c.id}/overview.pdf`, `${c.name.replace(/\s/g, "_")}_Kursuebersicht.pdf`)}
          data-testid="pro-overview-pdf" className="mt-5 border border-white/20 text-white font-heading uppercase tracking-wider px-4 py-2.5 text-xs hover:border-[#ff0033] transition-colors inline-flex items-center gap-2"><FileDown size={14} /> Kursübersicht PDF</button>
      </div>

      {/* Termine */}
      <h2 className="font-heading text-xl uppercase tracking-tight mt-8 mb-3">Termine</h2>
      <div className="flex flex-wrap gap-2 mb-5">
        {c.sessions.map((s) => {
          const cancelled = s.status === "cancelled";
          return (
            <button key={s.id} onClick={() => setSel(s.id)} data-testid={`pro-session-${s.id}`}
              className={`px-3 py-2 text-xs border transition-colors ${sel === s.id ? "border-[#ff0033] text-white bg-[#ff0033]/10" : "border-white/15 text-neutral-400 hover:text-white"} ${cancelled ? "line-through opacity-60" : ""}`}>
              {fmtDateTime(s.start)} {s.title ? `· ${s.title}` : ""} {cancelled ? "(abgesagt)" : ""}
            </button>
          );
        })}
      </div>

      {/* Teilnehmerliste */}
      {curSession && (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div>
            <h3 className="font-heading text-lg uppercase tracking-tight">Teilnehmerliste <span className="text-[#ff0033]">({totalPersons})</span></h3>
            <p className="text-neutral-500 text-xs" data-testid="pro-participant-summary">
              {parts.length} Anmeldung{parts.length === 1 ? "" : "en"} · {totalPersons} Teilnehmer gesamt{waitPersons > 0 ? ` · ${waitPersons} auf Warteliste` : ""}
            </p>
            {curSession.title && <p className="text-neutral-500 text-xs">Trainingsinhalt: {curSession.title}{curSession.description ? ` – ${curSession.description}` : ""}</p>}
          </div>
          <div className="flex gap-2">
            <button onClick={() => nav(`/pro/print/${sel}`)} data-testid="pro-print-btn" className="border border-white/20 text-white font-heading uppercase tracking-wider px-4 py-2.5 text-xs hover:border-[#ff0033] transition-colors inline-flex items-center gap-2"><Printer size={14} /> Drucken</button>
            <button onClick={() => downloadProPdf(`/pro/sessions/${sel}/participants.pdf`, `${c.name.replace(/\s/g, "_")}_${dt}_${coach}_Teilnehmerliste.pdf`)} data-testid="pro-participants-pdf" className="bg-[#ff0033] text-white font-heading uppercase tracking-wider px-4 py-2.5 text-xs hover:bg-[#cc0029] transition-colors inline-flex items-center gap-2"><FileDown size={14} /> PDF</button>
          </div>
        </div>
      )}

      {/* Desktop-Tabelle */}
      <div className="hidden md:block bg-[#0F0F0F] border border-white/10 overflow-x-auto">
        <table className="w-full text-sm" data-testid="pro-participant-table">
          <thead>
            <tr className="bg-[#1A1A1A] text-neutral-400 font-mono-lab text-[10px] uppercase tracking-widest">
              <th className="text-left p-3">#</th><th className="text-left p-3">Name</th><th className="text-left p-3">Teiln.</th><th className="text-left p-3">Telefon</th>
              <th className="text-left p-3">E-Mail</th><th className="text-left p-3">Adresse</th><th className="text-left p-3">Mitgl.</th><th className="text-left p-3">Gebucht</th>
              <th className="text-left p-3">Zahlung</th><th className="text-left p-3">Bemerkung</th>
            </tr>
          </thead>
          <tbody>
            {parts.map((p) => (
              <tr key={p.booking_number || p.n} className="border-t border-white/5 text-neutral-200">
                <td className="p-3 text-neutral-500">{p.n}</td>
                <td className="p-3 font-medium">{p.last_name}, {p.first_name}
                  {p.waitlist && <span className="ml-2 font-mono-lab text-[9px] uppercase tracking-widest text-amber-400 border border-amber-400/40 px-1.5 py-0.5">Warteliste</span>}
                </td>
                <td className="p-3" data-testid={`pro-persons-${p.booking_number || p.n}`}>
                  <span className={`font-mono-lab ${(p.persons || 1) > 1 ? "text-[#ff0033]" : "text-neutral-400"}`}>{p.persons || 1}</span>
                </td>
                <td className="p-3">{p.phone ? <a href={`tel:${p.phone}`} className="hover:text-[#ff0033]">{p.phone}</a> : "—"}</td>
                <td className="p-3">{p.email ? <a href={`mailto:${p.email}`} className="hover:text-[#ff0033]">{p.email}</a> : "—"}</td>
                <td className="p-3 text-neutral-400">{p.address || "—"}</td>
                <td className="p-3">{p.member ? "Ja" : "—"}</td>
                <td className="p-3 text-neutral-400">{fmtDate(p.booking_date)}</td>
                <td className={`p-3 ${payColor(p.payment_status)}`}>{payLabel(p.payment_status)}</td>
                <td className="p-3 text-neutral-400">{p.note || "—"}</td>
              </tr>
            ))}
            {parts.length === 0 && <tr><td colSpan={10} className="p-6 text-center text-neutral-500">Noch keine Teilnehmer für diesen Termin.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Mobile-Karten */}
      <div className="md:hidden space-y-3" data-testid="pro-participant-cards">
        {parts.map((p) => (
          <div key={p.booking_number || p.n} className="bg-[#0F0F0F] border border-white/10 p-4">
            <div className="flex items-center justify-between">
              <p className="font-heading text-lg text-white">{p.first_name} {p.last_name}</p>
              <span className={`text-xs ${payColor(p.payment_status)}`}>{payLabel(p.payment_status)}</span>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <span className="font-mono-lab text-[10px] uppercase tracking-widest text-white bg-[#ff0033]/15 border border-[#ff0033]/40 px-2 py-0.5">Teilnehmeranzahl: {p.persons || 1}</span>
              {p.waitlist && <span className="font-mono-lab text-[10px] uppercase tracking-widest text-amber-400 border border-amber-400/40 px-2 py-0.5">Warteliste</span>}
            </div>
            <div className="mt-2 flex flex-col gap-2 text-sm">
              {p.phone && <a href={`tel:${p.phone}`} className="flex items-center gap-2 text-neutral-300"><Phone size={15} className="text-[#ff0033]" /> {p.phone}</a>}
              {p.email && <a href={`mailto:${p.email}`} className="flex items-center gap-2 text-neutral-300 break-all"><Mail size={15} className="text-[#ff0033]" /> {p.email}</a>}
              {p.address && <span className="flex items-center gap-2 text-neutral-300"><MapPin size={15} className="text-[#ff0033]" /> {p.address}</span>}
              <span className="text-neutral-500 text-xs">Gebucht: {fmtDate(p.booking_date)} · Mitglied: {p.member ? "Ja" : "Nein"}</span>
              {p.note && <span className="text-neutral-400 text-xs">Bemerkung: {p.note}</span>}
            </div>
          </div>
        ))}
        {parts.length === 0 && <p className="text-neutral-500 text-center py-6">Noch keine Teilnehmer.</p>}
      </div>

      <p className="text-neutral-600 text-xs mt-4">Zuletzt aktualisiert: {new Date(data.updated_at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" })} Uhr</p>
    </div>
  );
};

export default ProCourseDetail;
